<template>
    <div ref="rootRef"
         class="lfm-select"
         :class="{
             'lfm-select--open': open,
             'lfm-select--multiple': multiple,
             'lfm-select--disabled': disabled,
         }">
        <button :id="triggerId"
                ref="triggerRef"
                type="button"
                class="lfm-select__trigger"
                role="combobox"
                aria-haspopup="listbox"
                :aria-expanded="open"
                :aria-controls="listboxId"
                :aria-activedescendant="activeOptionId"
                :aria-labelledby="labelledBy || undefined"
                :aria-label="!labelledBy ? accessibleName : undefined"
                :aria-disabled="disabled || undefined"
                :disabled="disabled"
                @click="toggle"
                @keydown.down.prevent="onArrowKey(1)"
                @keydown.up.prevent="onArrowKey(-1)"
                @keydown.home.prevent="onHomeEnd('home')"
                @keydown.end.prevent="onHomeEnd('end')"
                @keydown.enter.prevent="onEnter"
                @keydown.space.prevent="onSpace"
                @keydown.escape.prevent.stop="onEscape"
                @keydown="onTypeahead">
            <span class="lfm-select__label"
                  :class="{ 'lfm-select__label--placeholder': !hasSelection }">{{
                    hasSelection ? displayLabel : placeholderText
                }}</span>
            <span class="lfm-select__caret" aria-hidden="true">&#x25BE;</span>
        </button>

        <Teleport to="body">
            <div v-if="open" ref="panelRef" class="lfm-select__panel" :style="panelStyle">
                <input v-if="searchable"
                       ref="searchRef"
                       v-model="search"
                       type="text"
                       class="lfm-select__search"
                       :placeholder="texts.searchPlaceholder"
                       :aria-label="texts.searchPlaceholder"
                       :aria-controls="listboxId"
                       :aria-activedescendant="activeOptionId"
                       @keydown.down.prevent="onArrowKey(1)"
                       @keydown.up.prevent="onArrowKey(-1)"
                       @keydown.enter.prevent="onEnter"
                       @keydown.escape.prevent.stop="onEscape"/>
                <ul :id="listboxId"
                    ref="listRef"
                    class="lfm-select__options"
                    role="listbox"
                    :aria-multiselectable="multiple"
                    :aria-labelledby="labelledBy || undefined"
                    :aria-label="!labelledBy ? accessibleName : undefined">
                    <li v-for="(opt, idx) in filteredOptions"
                        :id="optionId(idx)"
                        :key="opt.value + ':' + idx"
                        class="lfm-select__option"
                        :class="{
                            'lfm-select__option--selected': isSelected(opt),
                            'lfm-select__option--active': idx === activeIndex,
                            'lfm-select__option--disabled': opt.disabled,
                        }"
                        role="option"
                        :aria-selected="isSelected(opt)"
                        :aria-disabled="opt.disabled || undefined"
                        @click="pick(opt)"
                        @mouseenter="activeIndex = idx">
                        <span class="lfm-select__option-label">{{ opt.label }}</span>
                    </li>
                    <li v-if="!filteredOptions.length"
                        class="lfm-select__option lfm-select__option--empty"
                        role="option"
                        aria-disabled="true">{{ texts.emptyText }}
                    </li>
                </ul>
            </div>
        </Teleport>
    </div>
</template>

<script setup>
import {computed, nextTick, onMounted, ref, toRef, useId} from 'vue';
import {useAccessibleName} from './composables/useAccessibleName.js';
import {useNativeSelect} from './composables/useNativeSelect.js';
import {usePanelPosition} from './composables/usePanelPosition.js';
import {useKeyboardNav} from './composables/useKeyboardNav.js';
import {useOutsideClick} from './composables/useOutsideClick.js';

const props = defineProps({
    nativeSelect: {
        type: Object,
        required: true,
        validator: (v) => v && v.tagName === 'SELECT',
    },
    placeholder: {type: String, default: ''},
    searchable: {type: Boolean, default: false},
    texts: {type: Object, required: true},
});

const uid = useId();
const triggerId = `${uid}-trigger`;
const listboxId = `${uid}-listbox`;
const optionId = (idx) => `${uid}-opt-${idx}`;

const rootRef = ref(null);
const triggerRef = ref(null);
const panelRef = ref(null);
const searchRef = ref(null);
const listRef = ref(null);

const open = ref(false);
const search = ref('');

const {
    options,
    selectedValues,
    multiple,
    disabled,
    pick: pickNative,
    deselect,
    hasExplicitSelection,
} = useNativeSelect(props.nativeSelect);

const searchable = toRef(props, 'searchable');

const placeholderText = computed(() => {
    if (props.placeholder) return props.placeholder;
    const emptyOption = options.value.find((o) => !o.value);
    if (emptyOption) return emptyOption.label;
    return props.texts.placeholder;
});

const {accessibleName, labelledBy} = useAccessibleName(
    props.nativeSelect,
    uid,
    placeholderText,
);

const {panelStyle, updatePanelPosition} = usePanelPosition(triggerRef, open);

const filteredOptions = computed(() => {
    if (!searchable.value || !search.value.trim()) return options.value;
    const q = search.value.trim().toLowerCase();
    return options.value.filter((o) => o.label.toLowerCase().includes(q));
});

const hasSelection = computed(() =>
    multiple.value
        ? selectedValues.value.length > 0
        : !!selectedValues.value[0],
);

const selectedOptions = computed(() =>
    options.value.filter((o) => selectedValues.value.includes(o.value)),
);

const displayLabel = computed(() => {
    if (multiple.value) {
        return selectedOptions.value.map((o) => o.label).join(', ');
    }
    const match = options.value.find((o) => o.value === selectedValues.value[0]);
    return match ? match.label : '';
});

function isSelected(opt) {
    return selectedValues.value.includes(opt.value);
}

function toggle() {
    if (disabled.value) return;
    open.value ? close() : openPanel();
}

function openPanel() {
    if (disabled.value) return;
    open.value = true;
    activeIndex.value = initialActiveIndex();
    nextTick(() => {
        updatePanelPosition();
        nextTick(() => {
            if (searchable.value && searchRef.value) {
                searchRef.value.focus({preventScroll: true});
            } else {
                triggerRef.value?.focus({preventScroll: true});
            }
            scrollActiveIntoView();
        });
    });
}

function close() {
    open.value = false;
    search.value = '';
    activeIndex.value = -1;
    nextTick(() => triggerRef.value?.focus());
}

function pick(opt) {
    pickNative(opt);
    if (!multiple.value) close();
}

const {
    activeIndex,
    initialActiveIndex,
    scrollActiveIntoView,
    onArrowKey,
    onHomeEnd,
    onEnter,
    onSpace,
    onEscape,
    onTypeahead,
} = useKeyboardNav({
    open,
    openPanel,
    close,
    toggle,
    searchable,
    filteredOptions,
    listRef,
    selectedValues,
    search,
    pick,
});

const activeOptionId = computed(() => {
    if (!open.value || activeIndex.value < 0) return undefined;
    if (activeIndex.value >= filteredOptions.value.length) return undefined;
    return optionId(activeIndex.value);
});

useOutsideClick(open, [rootRef, panelRef], close);

onMounted(() => {
    if (props.placeholder && !multiple.value && !hasExplicitSelection()) {
        deselect();
    }
});
</script>

<style>
.lfm-select {
    --lfm-select-bg: #fff;
    --lfm-select-border: #ccc;
    --lfm-select-border-focus: #888;
    --lfm-select-radius: 4px;
    --lfm-select-padding-y: 0.5rem;
    --lfm-select-padding-x: 0.75rem;
    --lfm-select-color: #222;
    --lfm-select-placeholder: #888;
    --lfm-select-disabled-bg: #f7f7f7;
    --lfm-select-disabled-color: #999;

    position: relative;
    display: inline-block;
    width: 100%;
    color: var(--lfm-select-color);
}

.lfm-select__trigger {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 0.5rem;
    width: 100%;
    padding: var(--lfm-select-padding-y) var(--lfm-select-padding-x);
    background: var(--lfm-select-bg);
    border: 1px solid var(--lfm-select-border);
    border-radius: var(--lfm-select-radius);
    font: inherit;
    color: inherit;
    text-align: left;
    cursor: pointer;
    appearance: none;
}

.lfm-select__trigger:focus-visible {
    outline: 2px solid var(--lfm-select-border-focus);
    outline-offset: 2px;
}

.lfm-select--open .lfm-select__trigger {
    border-color: var(--lfm-select-border-focus);
}

.lfm-select--disabled .lfm-select__trigger {
    background: var(--lfm-select-disabled-bg);
    color: var(--lfm-select-disabled-color);
    cursor: not-allowed;
}

.lfm-select__label {
    flex: 1;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.lfm-select__label--placeholder {
    color: var(--lfm-select-placeholder);
}

.lfm-select__caret {
    flex: 0 0 auto;
    transition: transform 0.15s ease;
}

.lfm-select--open .lfm-select__caret {
    transform: rotate(180deg);
}

.lfm-select__panel {
    --lfm-select-panel-bg: #fff;
    --lfm-select-panel-border: #ccc;
    --lfm-select-panel-radius: 4px;
    --lfm-select-panel-shadow: 0 4px 16px rgba(0, 0, 0, 0.12);
    --lfm-select-option-hover: #f3f3f3;
    --lfm-select-option-active: #ececec;
    --lfm-select-option-selected: #e5efff;
    --lfm-select-option-selected-color: #1a4fa0;
    --lfm-select-placeholder: #888;

    background: var(--lfm-select-panel-bg);
    border: 1px solid var(--lfm-select-panel-border);
    border-radius: var(--lfm-select-panel-radius);
    box-shadow: var(--lfm-select-panel-shadow);
    overflow: hidden;
    font: inherit;
}

.lfm-select__search {
    width: 100%;
    box-sizing: border-box;
    padding: 0.5rem 0.75rem;
    border: 0;
    border-bottom: 1px solid var(--lfm-select-panel-border);
    font: inherit;
    color: inherit;
    outline: none;
}

.lfm-select__search:focus {
    background: var(--lfm-select-option-hover);
}

.lfm-select__options {
    list-style: none;
    margin: 0;
    padding: 0.25rem 0;
    max-height: 240px;
    overflow-y: auto;
}

.lfm-select__option {
    padding: 0.5rem 0.75rem;
    cursor: pointer;
    user-select: none;
}

.lfm-select__option:hover,
.lfm-select__option--active {
    background: var(--lfm-select-option-active);
}

.lfm-select__option--selected {
    background: var(--lfm-select-option-selected);
    color: var(--lfm-select-option-selected-color);
    font-weight: 500;
}

.lfm-select__option--selected.lfm-select__option--active {
    background: var(--lfm-select-option-selected);
    filter: brightness(0.97);
}

.lfm-select__option--disabled {
    color: var(--lfm-select-placeholder);
    cursor: not-allowed;
}

.lfm-select__option--empty {
    color: var(--lfm-select-placeholder);
    cursor: default;
    font-style: italic;
}

.lfm-select__option--empty:hover {
    background: transparent;
}
</style>
